import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from "@tanstack/react-query";

import type {
  GetOnboardingModulesResponse,
  GetOnboardingProgressResponse,
  PutOnboardingProgressBody,
  PutOnboardingProgressResponse
} from "@/types/onboarding";
import { CustomError } from "@/utils/errors";

import { putOnboardingProgress } from "./onboardingApi";
import {
  onboardingModulesQuery,
  onboardingOverviewQuery,
  type OnboardingOverviewResponse,
  onboardingProgressQuery,
  upsertOnboardingProgressCache
} from "./onboardingQueries";

export const useGetOnboardingModulesAPI = (
  options?: Omit<
    UseQueryOptions<
      GetOnboardingModulesResponse,
      CustomError,
      GetOnboardingModulesResponse
    >,
    "queryKey" | "queryFn"
  >
) =>
  useQuery({
    ...onboardingModulesQuery(),
    ...options
  });

export const useGetOnboardingProgressAPI = (
  moduleId?: string,
  options?: Omit<
    UseQueryOptions<
      GetOnboardingProgressResponse,
      CustomError,
      GetOnboardingProgressResponse
    >,
    "queryKey" | "queryFn"
  >
) =>
  useQuery({
    ...onboardingProgressQuery(moduleId),
    ...options
  });

export const useGetOnboardingOverviewAPI = (
  options?: Omit<
    UseQueryOptions<
      OnboardingOverviewResponse,
      CustomError,
      OnboardingOverviewResponse
    >,
    "queryKey" | "queryFn"
  >
) => {
  const queryClient = useQueryClient();
  return useQuery({
    ...onboardingOverviewQuery(queryClient),
    ...options
  });
};

export const useUpdateOnboardingProgressAPI = (
  options?: UseMutationOptions<
    PutOnboardingProgressResponse,
    CustomError,
    { moduleId: string; body: PutOnboardingProgressBody }
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleId, body }) => putOnboardingProgress(moduleId, body),
    ...options,
    onSuccess: (...args) => {
      const [data] = args;
      upsertOnboardingProgressCache(queryClient, data);
      return options?.onSuccess?.(...args);
    }
  });
};
